/**
 * Telefono.jsx — la testata e la barra in basso della vista del medico al
 * telefono (proposta telefono-stile-nuovo, punto 4, VARIANTE B; Alex,
 * 28/10/2026, riferita da Claude nel canale). Solo nello stile nuovo, sotto i
 * 480 px compresi (T1), per un utente collegato a un medico: lo decide il Layout con
 * vistaTelefonoDelMedico (navigazione/telefono.js). Sopra, con lo stile di
 * prima o per gli altri ruoli, il guscio e' quello di sempre.
 *
 *   - TestataTelefono: chiara, il titolo della schermata (l'etichetta della
 *     tabella delle schermate, mai un dato) e la riga di riepilogo (data,
 *     pazienti del giorno, in attesa: i numeri di GET /api/oggi, contati dal
 *     server). A destra il tondo con le iniziali, che apre il menu (il
 *     cassetto di sempre), «Il mio account», l'assistente ed «Esci»: il
 *     pulsante dell'assistente non copre la barra in basso;
 *   - BarraTelefono: cinque posti - Oggi, Agenda, «Scatta» al centro,
 *     Pazienti, Compiti col numero del menu. Le voci vengono dalla tabella
 *     delle schermate e dalle sue guardie (postiBarra): un ruolo che non apre
 *     una schermata ha il posto vuoto;
 *   - ScegliPaziente: «Scatta» fuori da una scheda chiede prima il paziente,
 *     con in cima i pazienti di oggi e sotto la ricerca, poi apre il pannello
 *     del punto 1 (CaricaDalTelefono) per quel paziente. Dentro una scheda la
 *     barra scatta per il paziente della scheda (registraScatto).
 *   - CercaPaziente: il campo «Cerca un paziente (nome o codice fiscale)»,
 *     con la ricerca della pagina Pazienti (hooks/useRicercaPazienti.js: la
 *     stessa rotta, la stessa traccia nel registro degli accessi).
 *
 * GLI ATTRIBUTI PER LE PROVE: data-testata-telefono, data-titoli-telefono
 * (il titolo e il riepilogo, senza il tondo e la ricerca), data-riepilogo,
 * data-tondo; data-barra-telefono, data-posto (il percorso, o «scatta»),
 * data-posto-vuoto, data-contatore; data-scegli-paziente, data-di-oggi,
 * data-trovato; data-cerca-paziente. Le prove cercano quelli, non le classi.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Camera, Search, UserRound, LogOut, Sparkles, Menu as IconaMenu } from 'lucide-react';
import { schermataDi, siApre, voceAccesa } from '../../navigazione/schermate';
import { postiBarra, riepilogoGiornata, pazientiDiOggi, scattaNellaScheda } from '../../navigazione/telefono';
import useRicercaPazienti from '../../hooks/useRicercaPazienti';
import { MINIMO_PAZIENTI } from '../../navigazione/comandi';
import { oraIta } from '../../utils/oraItaliana';
import CaricaDalTelefono from '../pazienti/CaricaDalTelefono';
import '../../styles/TelefonoConsole.css';

const nomeDi = (p) => [p.cognome, p.nome].filter(Boolean).join(' ') || '—';

/* ------------------------------------------------------------------ */

export function TestataTelefono({ titolo, giornata, utente, onMenu, onAccount, onAssistente, onEsci, children }) {
    const [aperto, setAperto] = useState(false);
    const tondo = useRef(null);
    const iniziali = `${utente?.nome?.charAt(0) || ''}${utente?.cognome?.charAt(0) || ''}`.toUpperCase();
    const riepilogo = riepilogoGiornata(giornata);

    /* Esc chiude il menu del tondo e riporta il fuoco al tondo. */
    useEffect(() => {
        if (!aperto) return undefined;
        const esc = (e) => { if (e.key === 'Escape') { setAperto(false); tondo.current?.focus(); } };
        document.addEventListener('keydown', esc);
        return () => document.removeEventListener('keydown', esc);
    }, [aperto]);

    const scegli = (gesto) => { setAperto(false); gesto(); };

    return (
        <header className="tg-testata" data-testata-telefono="">
            <div className="tg-testata-riga">
                <div className="tg-testata-titoli" data-titoli-telefono="">
                    <h1 className="tg-titolo">{titolo}</h1>
                    {riepilogo && <span className="tg-riepilogo rm-tronca" data-riepilogo="" title={riepilogo}>{riepilogo}</span>}
                </div>
                <button type="button" ref={tondo} className="tg-tondo" data-tondo=""
                        aria-label="Menu e account" aria-haspopup="menu" aria-expanded={aperto}
                        onClick={() => setAperto((x) => !x)}>
                    {iniziali || <UserRound size={18} strokeWidth={1.8} aria-hidden="true" />}
                </button>
            </div>
            {aperto && (
                <>
                    <div className="tg-velo" aria-hidden="true" onClick={() => setAperto(false)} />
                    <div className="tg-menu-tondo" role="menu" aria-label="Menu e account">
                        {/* aria-controls: questa voce apre il cassetto del menu (#guscio-menu), come il
                            pulsante «Menu» della barra in alto, che qui non si vede. E' la porta del
                            menu che le prove cercano sul telefono (scripts/percorsi/guscio.js). */}
                        <button type="button" role="menuitem" className="tg-voce-tondo" aria-controls="guscio-menu"
                                onClick={() => scegli(onMenu)}>
                            <IconaMenu size={18} strokeWidth={1.8} aria-hidden="true" /><span>Menu</span>
                        </button>
                        <button type="button" role="menuitem" className="tg-voce-tondo" onClick={() => scegli(onAccount)}>
                            <UserRound size={18} strokeWidth={1.8} aria-hidden="true" /><span>Il mio account</span>
                        </button>
                        <button type="button" role="menuitem" className="tg-voce-tondo" onClick={() => scegli(onAssistente)}>
                            <Sparkles size={18} strokeWidth={1.8} aria-hidden="true" /><span>Assistenza</span>
                        </button>
                        <button type="button" role="menuitem" className="tg-voce-tondo" onClick={() => scegli(onEsci)}>
                            <LogOut size={18} strokeWidth={1.8} aria-hidden="true" /><span>Esci</span>
                        </button>
                    </div>
                </>
            )}
            {children}
        </header>
    );
}

/* ------------------------------------------------------------------ */

export function BarraTelefono({ utente, schermata, contatori, onVai, onScatta }) {
    const posti = postiBarra(utente, schermataDi, siApre);
    const accesa = voceAccesa(schermata);
    return (
        <nav className="tg-barra" data-barra-telefono="" aria-label="Navigazione principale">
            {posti.map(({ posto, schermata: s, scatta }) => {
                if (scatta) {
                    return (
                        <button key={posto} type="button" className="tg-posto tg-posto--scatta" data-posto="scatta"
                                aria-label="Scatta una foto o un documento" onClick={onScatta}>
                            <span className="tg-scatta-tondo" aria-hidden="true">
                                <Camera size={26} strokeWidth={1.8} />
                            </span>
                            <span className="tg-posto-nome">Scatta</span>
                        </button>
                    );
                }
                if (!s) return <span key={posto} className="tg-posto tg-posto--vuoto" data-posto={posto} data-posto-vuoto="" aria-hidden="true" />;
                const { Icona, etichetta, contatore } = s;
                const numero = contatore && contatori && contatori[contatore] > 0 ? contatori[contatore] : null;
                const attiva = accesa === s.percorso;
                return (
                    <button key={posto} type="button" className={`tg-posto ${attiva ? 'tg-posto--attivo' : ''}`}
                            data-posto={posto} aria-current={attiva ? 'page' : undefined}
                            onClick={() => onVai(s.percorso)}>
                        <span className="tg-posto-icona" aria-hidden="true">
                            {Icona && <Icona size={22} strokeWidth={1.8} />}
                            {numero !== null && <span className="tg-numero rm-numeri" data-contatore={contatore}>{numero}</span>}
                        </span>
                        <span className="tg-posto-nome">{etichetta}</span>
                    </button>
                );
            })}
        </nav>
    );
}

/* ------------------------------------------------------------------ */

/** Il campo di ricerca e i pazienti trovati. `onScegli(p)` riceve la riga di /api/pazienti. */
export function CercaPaziente({ onScegli, autofocus = false }) {
    const [testo, setTesto] = useState('');
    const esito = useRicercaPazienti(testo);
    const campo = useRef(null);
    useEffect(() => { if (autofocus && campo.current) campo.current.focus(); }, [autofocus]);
    const corto = testo.trim().length > 0 && testo.trim().length < MINIMO_PAZIENTI;
    return (
        <div className="tg-cerca" data-cerca-paziente="">
            <label className="tg-cerca-campo">
                <Search size={18} strokeWidth={1.8} aria-hidden="true" />
                {/* Una casella di ricerca non trattiene la ricarica della
                    pagina (la terza difesa): type="search" e data-non-trattiene. */}
                <input ref={campo} type="search" value={testo} data-non-trattiene=""
                       placeholder="Cerca un paziente (nome o codice fiscale)" aria-label="Cerca un paziente"
                       onChange={(e) => setTesto(e.target.value)} />
            </label>
            {corto && <p className="tg-nota">Scrivi almeno {MINIMO_PAZIENTI} caratteri.</p>}
            {esito.stato === 'cerco' && <p className="tg-nota">Cerco…</p>}
            {esito.stato === 'errore' && <p className="tg-nota tg-nota--errore" role="alert">{esito.messaggio}</p>}
            {esito.stato === 'fatto' && esito.elenco.length === 0 && <p className="tg-nota">Nessun paziente trovato.</p>}
            {esito.stato === 'fatto' && esito.elenco.length > 0 && (
                <ul className="tg-elenco">
                    {esito.elenco.map((p) => (
                        <li key={p.id}>
                            <button type="button" className="tg-riga" data-trovato={p.id} onClick={() => onScegli(p)}>
                                <span className="tg-riga-testo">
                                    <span className="tg-riga-nome rm-tronca">{nomeDi(p)}</span>
                                    {p.id_paziente && <span className="tg-riga-sotto rm-numeri">{p.id_paziente}</span>}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */

/**
 * «Scatta» fuori da una scheda: prima il paziente (in cima quelli di oggi,
 * dalle righe dell'agenda della rotta; sotto la ricerca), poi il pannello
 * del punto 1 per lui, aperto sulla fotocamera. Si chiude quando il pannello
 * si chiude (con le sue regole: niente si perde in silenzio).
 */
export function ScegliPaziente({ giornata, onChiudi }) {
    const [paziente, setPaziente] = useState(null);
    if (paziente) {
        return <CaricaDalTelefono pazienteId={paziente.id} perChi={paziente.nome}
                                  barra="nessuna" avvio="scatta" onChiuso={onChiudi} />;
    }
    const diOggi = pazientiDiOggi(giornata?.agenda);
    return (
        <div className="tg-scegli" role="dialog" aria-modal="true" aria-label="Per quale paziente?" data-scegli-paziente="">
            <div className="tg-scegli-testa">
                <h2 className="tg-scegli-titolo">Per quale paziente?</h2>
                <button type="button" className="tg-pulsante" onClick={onChiudi}>Chiudi</button>
            </div>
            {diOggi.length > 0 && (
                <section aria-labelledby="tg-di-oggi">
                    <h3 id="tg-di-oggi" className="tg-etichetta">I miei pazienti di oggi</h3>
                    <ul className="tg-elenco">
                        {diOggi.map((r) => (
                            <li key={r.paziente_id}>
                                <button type="button" className="tg-riga" data-di-oggi={r.paziente_id}
                                        onClick={() => setPaziente({ id: r.paziente_id, nome: r.paziente || '' })}>
                                    <span className="tg-riga-ora rm-numeri">{oraIta(r.inizio)}</span>
                                    <span className="tg-riga-testo">
                                        <span className="tg-riga-nome rm-tronca">{r.paziente || '—'}</span>
                                        {r.cosa && <span className="tg-riga-sotto rm-tronca">{r.cosa}</span>}
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
            <section aria-label="Cerca un altro paziente">
                <h3 className="tg-etichetta">{diOggi.length ? 'Un altro paziente' : 'Cerca il paziente'}</h3>
                <CercaPaziente onScegli={(p) => setPaziente({ id: p.id, nome: nomeDi(p) })} />
            </section>
        </div>
    );
}

/** Il tocco su «Scatta»: la scheda aperta, se c'e'; altrimenti la scelta del paziente. */
export const toccaScatta = (apriScelta) => { if (!scattaNellaScheda()) apriScelta(); };
